import { useNavigate, useSearch } from '@tanstack/react-router';

/**
 * Drive a Mantine `Tabs` (or any segmented view) from a URL search param so the
 * sidebar deep-links, old-route redirects, and a browser refresh all land on the
 * right tab. Reads loosely (`strict: false`) and falls back to `fallback` for a
 * missing or unrecognised value; writes with `replace` so flipping tabs doesn't
 * pile up history entries.
 *
 * Each hub route should still declare `validateSearch` for the same param so the
 * redirect `search` object type-checks and the value is normalised at the route
 * boundary — this hook is the read/write convenience on top of that.
 */
export function useTabParam<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (value: T) => void] {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const raw = search[key];
  const value =
    typeof raw === 'string' && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;

  const setValue = (next: T) => {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, [key]: next }),
      replace: true,
    });
  };

  return [value, setValue];
}
